// Eskom PARCC — the Compliance Evaluation Sheet (CES) capture PAGE.
//
// 9 Jun alignment call: the CES modal was too cramped for comfortable finding
// entry, so the capture is now a full page at /ces/:ref. The WRITE flow is the
// CesModal's, unchanged in substance: the transaction's per-mechanism working
// paper is auto-resolved (NTCSA vs Other Divisions × mechanism) and seeds the
// criteria; the reviewer sets each criterion's status / finding; Save
// normalises the evaluation into four tables via `buildCesCapture` + the
// `useCaptureCes` mutation, guarded by `checkRowVersion`. No autosave.
//
// R3 additions over the old modal:
//   • The GREY HEADER BLOCK (cesHeaderFields): the template's header fields
//     rendered above the criteria — population fields locked, the four
//     OpenText-sourced fields (CSD Number, PR Number, Procurement Manager,
//     End-User) editable and persisted onto the transaction at save.
//   • The auto-resolved template is AUTHORITATIVE: the manual route picker only
//     renders when no template resolves for the transaction (A5).
//   • The OpenText button rides the page header (A6) — reviewers open the
//     document folder from the working-paper surface too.
//
// Permission: in-page guard via canEditTransaction (admin, or the assignee).
// NEW-transaction capture is no longer offered here — the register is
// import-populated and the old modal's new-tx form was unreachable from the UI.
import { useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  ConflictBanner,
  EmptyState,
  LoadingState,
  Select,
  useToast,
} from '../../components/ui';
import {
  CES_ROUTES,
  CES_STATUS_OPTIONS,
  TRANSACTION_COMPLIANCE_LEVELS,
  type CesRoute,
} from '../../domain/enums';
import { checkRowVersion } from '../../lib/concurrency';
import { hasRealLink } from '../../domain/links';
import { countNonCompliantCes, cesRowsToCompliant } from '../../domain/complianceCascade';
import type { AiProposal } from '../../lib/adminApi';
import { AiReviewPanel, AiProposalBlock, type ProposalMap } from './AiReviewPanel';
import { EvidenceViewer, type EvidenceCitation } from '../ai/EvidenceViewer';
import { Sparkles } from 'lucide-react';
import { Badge, complianceClass } from '../../components/ui/Badge';
import { cesMapStatusToRegister } from '../../domain/ces';
import { useAuth } from '../../lib/auth';
import { useCaptureCes, useTransactions } from '../../hooks';
import type { Transaction } from '../../data/types';
import { canEditTransaction } from '../transactions/editTransaction';
import { resolveWorkingPaperTemplate } from '../../domain/workingPapers/resolveTemplate';
import {
  buildCesCapture,
  seedCriteriaRows,
  seedCriteriaRowsFromTemplate,
  type CesCriterionRow,
  type CesTxFields,
} from './buildCesCapture';
import {
  cesHeaderFields,
  type EditableHeaderValues,
  type EditableHeaderKey,
} from './cesHeaderFields';

import { templateToCesRoute } from './templateRoute';

/** A reviewer-added gap draft (Transversal / Multisource) — `id` is a stable React key. */
interface GapDraft {
  id: number;
  description: string;
  /** A `CES_STATUS_OPTIONS` value. */
  status: string;
  contravention: string;
}

/** Resolve the :ref param to its transaction and gate access, then mount the capture. */
export function CesPage(): React.JSX.Element {
  const { ref } = useParams<{ ref: string }>();
  const referenceNo = ref ?? '';
  const navigate = useNavigate();
  const { grant, user } = useAuth();
  const txQuery = useTransactions();

  const tx = useMemo(
    () => (txQuery.data ?? []).find((t) => !t.deleted && t.referenceNo === referenceNo),
    [txQuery.data, referenceNo],
  );

  if (txQuery.isLoading) return <LoadingState message="Loading the transaction…" />;
  if (!tx) {
    return (
      <EmptyState
        message={`No transaction found for "${referenceNo}". It may have been removed, or you may not have access to it.`}
      />
    );
  }
  if (!canEditTransaction(tx, grant)) {
    return (
      <Card>
        <h3>Compliance Evaluation Sheet</h3>
        <p className="small">
          This working paper is read-only for you: you may capture only transactions allocated to
          you. Ask an administrator to reallocate {tx.referenceNo} if you should be its reviewer.
        </p>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Card>
    );
  }

  // Keyed by transaction id so all capture state resets if the route changes.
  return (
    <CesCapture
      key={tx.id}
      tx={tx}
      capturedByEmail={user?.email ?? ''}
      onDone={() => navigate(-1)}
    />
  );
}

/** The full-page capture form for a resolved, editable transaction. */
function CesCapture({
  tx,
  capturedByEmail,
  onDone,
}: {
  tx: Transaction;
  capturedByEmail: string;
  onDone: () => void;
}): React.JSX.Element {
  const { show } = useToast();
  const capture = useCaptureCes();

  // Owner feedback points 5 + 6, now authoritative (A5): resolve the
  // transaction's per-mechanism working paper and seed the page with THOSE
  // criteria. The manual route picker renders only when nothing resolves.
  const template = useMemo(() => resolveWorkingPaperTemplate(tx), [tx]);

  const [route, setRoute] = useState<CesRoute | ''>(() =>
    template ? templateToCesRoute(template.mechanism) : '',
  );
  const [rows, setRows] = useState<CesCriterionRow[]>(() =>
    template ? seedCriteriaRowsFromTemplate(template) : [],
  );
  // AI proposals, keyed by criterion seq. Client state only — the engine writes
  // nothing, so a run that is never saved simply disappears.
  const [proposals, setProposals] = useState<ProposalMap>(() => new Map());
  const [confirmed, setConfirmed] = useState<ReadonlySet<number>>(() => new Set());
  const [sortByConfidence, setSortByConfidence] = useState(false);
  /**
   * The open citation, if any. ONE viewer for the whole page, not one per
   * proposal row: `Flyout` registers a document-level Escape listener and
   * portals its own scrim, so 33 mounted instances would mean 33 listeners and
   * 33 stacked scrims.
   */
  const [openCitation, setOpenCitation] = useState<EvidenceCitation | null>(null);
  /** Findings the engine raised OUTSIDE the checklist. They attach to no
   *  criterion, so they cannot live in `proposals` (keyed by seq) and are
   *  rendered after the criteria table. */
  const [holistic, setHolistic] = useState<AiProposal[]>([]);
  /** The current run's model + id, for provenance on a confirmed proposal. */
  const [aiRunMeta, setAiRunMeta] = useState<{ runId: string; model: string | null } | null>(null);

  /** The provenance block to stamp on a row when its AI proposal is confirmed. */
  const aiMetaFor = useCallback(
    (p: AiProposal): CesCriterionRow['ai'] => ({
      pfmaSection: p.pfmaSection,
      citations: p.citations,
      confidence: p.confidence,
      model: aiRunMeta?.model ?? null,
      runId: aiRunMeta?.runId ?? null,
    }),
    [aiRunMeta],
  );
  // The criteria the engine is asked to assess. Built from the same seeded rows
  // the reviewer sees, so the two can never describe different rubrics.
  const aiCriteria = useMemo(
    () => rows.map((r) => ({ seq: r.seq, label: r.criterion })),
    [rows],
  );

  /** How many proposed criteria have been decided — by confirming the proposal
   *  or by the reviewer setting the status themselves. Overriding via the
   *  dropdown counts as decided; the engine's job was to offer, not to win. */
  const decidedCount = useMemo(
    () =>
      rows.filter((r) => proposals.has(r.seq) && (confirmed.has(r.seq) || r.status !== '')).length,
    [rows, proposals, confirmed],
  );

  /** Which proposed criteria the reviewer has already actioned (confirmed, or a
   *  status set by hand) — the summary greys these out. */
  const decidedSeqs = useMemo(
    () => new Set(rows.filter((r) => confirmed.has(r.seq) || r.status !== '').map((r) => r.seq)),
    [rows, confirmed],
  );

  /**
   * Accept every proposal the reviewer has NOT already decided.
   *
   * ⚠️ SKIPS DECIDED ROWS. The per-row confirm overwrites `status`
   * unconditionally, which is right when a human clicked that row's button.
   * Reused naively in a loop it would silently discard every manual override —
   * exactly the failure the human-in-the-loop guardrail exists to prevent. Only
   * the rows actually applied are marked "Confirmed by you", so provenance stays
   * honest about which verdicts the engine supplied.
   *
   * One setRows pass rather than N patchRow calls: N sequential updates over the
   * same array is a lost-update race waiting to happen.
   */
  const confirmProposals = useCallback(
    (only?: number[]) => {
      const want = only ? new Set(only) : null;
      const applied: number[] = [];
      setRows((rs) =>
        rs.map((r) => {
          const p = proposals.get(r.seq);
          if (!p || confirmed.has(r.seq) || r.status !== '') return r;
          if (want && !want.has(r.seq)) return r;
          applied.push(r.seq);
          return {
            ...r,
            status: p.verdict as CesCriterionRow['status'],
            finding: r.finding.trim() === '' ? p.rationale : r.finding,
            ai: aiMetaFor(p),
          };
        }),
      );
      setConfirmed((s) => {
        const next = new Set(s);
        for (const seq of applied) next.add(seq);
        return next;
      });
    },
    [proposals, confirmed, aiMetaFor],
  );

  /**
   * The DISPLAY order. Never a mutation of `rows` — that array feeds
   * buildCesCapture, so sorting it in place would change the order the working
   * paper is saved in and break the stage grouping.
   */
  const displayRows = useMemo(() => {
    if (!sortByConfidence) return rows;
    // Un-proposed rows sort last: 2 is beyond any confidence.
    return [...rows].sort(
      (a, b) => (proposals.get(a.seq)?.confidence ?? 2) - (proposals.get(b.seq)?.confidence ?? 2),
    );
  }, [rows, proposals, sortByConfidence]);

  const [additional, setAdditional] = useState('');
  const [override, setOverride] = useState('');
  // Compliance Override cascade (owner feedback #9): when the override is set to
  // Compliant and non-compliant procedures remain, offer to flip them all at once.
  // Holds the count to confirm, or null when there is nothing to prompt.
  const [cascadeCount, setCascadeCount] = useState<number | null>(null);
  // The ACTUAL reviewer's name (owner feedback #8): coordinators capture on their
  // behalf, so record who reviewed it. Typed per capture (the name lives on the
  // working paper, not the transaction, so there is nothing on `tx` to seed from).
  const [reviewerName, setReviewerName] = useState('');
  // Structured gaps (Transversal / Multisource templates only): the reviewer adds
  // gap rows below the fixed criteria — each with its own rated compliance level
  // and a contravention reference. buildCesCapture folds them into "Gaps
  // Identified" rows (see its `gaps` input).
  const [gaps, setGaps] = useState<GapDraft[]>([]);
  const gapId = useRef(0);

  // The grey header block's editable working copy (R3 #1) — the four
  // OpenText-sourced fields, seeded from the transaction.
  const [headerEdit, setHeaderEdit] = useState<EditableHeaderValues>(() => ({
    csdNumber: tx.csdNumber ?? '',
    prNumber: tx.prNumber ?? '',
    procurementManager: tx.procurementManager ?? '',
    endUser: tx.endUser ?? '',
  }));

  // Freeze the rowVersion the page opened against (concurrency precondition) —
  // a background refetch can advance it while the page stays mounted, so the
  // snapshot is captured once at mount, not tracked live.
  const [loadedRowVersion] = useState(tx.rowVersion);
  // A save conflict, surfaced as a banner (see handleSave). Cleared on a clean
  // save, on Reload latest, and on a successful overwrite.
  const [conflict, setConflict] = useState(false);

  const saving = capture.isPending;
  const headerFields = useMemo(
    () => cesHeaderFields(template?.headerFields, tx, headerEdit),
    [template, tx, headerEdit],
  );

  function patchRow(seq: number, patch: Partial<CesCriterionRow>): void {
    setRows((rs) => rs.map((r) => (r.seq === seq ? { ...r, ...patch } : r)));
  }

  // Compliance Override change: setting it to Compliant while non-compliant
  // procedures remain offers the one-shot cascade (owner feedback #9). Any other
  // value simply dismisses a pending prompt — the coordinator can still edit
  // procedures individually when only some gaps are addressed.
  function handleOverrideChange(value: string): void {
    setOverride(value);
    const n = value === 'Compliant' ? countNonCompliantCes(rows) : 0;
    setCascadeCount(n > 0 ? n : null);
  }

  function applyCascade(): void {
    setRows((rs) => cesRowsToCompliant(rs));
    setCascadeCount(null);
  }

  function patchHeader(key: EditableHeaderKey, value: string): void {
    setHeaderEdit((h) => ({ ...h, [key]: value }));
  }

  function addGap(): void {
    gapId.current += 1;
    const id = gapId.current;
    setGaps((g) => [...g, { id, description: '', status: '', contravention: '' }]);
  }
  function patchGap(id: number, patch: Partial<GapDraft>): void {
    setGaps((g) => g.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }
  function removeGap(id: number): void {
    setGaps((g) => g.filter((x) => x.id !== id));
  }

  function chooseRoute(next: CesRoute | ''): void {
    setRoute(next);
    setRows(next ? seedCriteriaRows(next) : []);
    // Manual-route templates never carry a gaps block.
    setGaps([]);
  }

  // The tx context buildCesCapture reads — the row's values, with the four
  // reviewer-entered header fields overriding what was on the transaction.
  const txFields = useMemo<CesTxFields>(
    () => ({
      dataset: tx.dataset,
      division: tx.division,
      supplier: tx.supplier,
      value: tx.value,
      valueDisplay: tx.valueDisplay,
      mechanism: tx.mechanism,
      year: tx.year,
      period: tx.period,
      link: tx.link,
      recordLabel: tx.recordLabel,
      baseNo: tx.baseNo,
      bu: tx.bu,
      briefDescription: tx.briefDescription,
      buyer: tx.buyer,
      contractNumber: tx.contractNumber,
      csdNumber: headerEdit.csdNumber,
      prNumber: headerEdit.prNumber,
      procurementManager: headerEdit.procurementManager,
      endUser: headerEdit.endUser,
    }),
    [tx, headerEdit],
  );

  async function handleSave(): Promise<void> {
    if (!route) {
      show('Choose a procurement route to seed the evaluation criteria.', 'error');
      return;
    }

    // Guard the optimistic-concurrency window BEFORE any write. A conflict means
    // someone changed the tx since the page opened — surface and leave so the
    // fresh state is re-read.
    let check;
    try {
      check = await checkRowVersion(tx.referenceNo, loadedRowVersion ?? tx.rowVersion);
    } catch (error) {
      show(
        error instanceof Error ? error.message : 'Could not verify the transaction. Please retry.',
        'error',
      );
      return;
    }
    if (!check.ok) {
      if (check.current === null) {
        // The row is gone — there is nothing to overwrite, so this stays terminal.
        show('This transaction no longer exists. Returning…', 'error');
        onDone();
        return;
      }
      // Someone saved a newer version. Surface it as a banner rather than a
      // toast — it needs a decision and must stay on screen until one is made.
      // There is deliberately NO overwrite path: see the note on ConflictBanner
      // and docs/concurrency-and-overwrite.md.
      setConflict(true);
      return;
    }
    setConflict(false);

    const payload = buildCesCapture({
      route,
      referenceNo: tx.referenceNo,
      rows,
      additionalFindings: additional,
      capturedByEmail,
      reviewerName: reviewerName.trim() || undefined,
      capturedDate: new Date().toISOString(),
      tx: txFields,
      complianceOverride: override || undefined,
      gaps: template?.supportsGaps
        ? gaps.map(({ description, status, contravention }) => ({
            description,
            status,
            contravention,
          }))
        : undefined,
    });

    // The capture hook OWNS its single error toast (onError); a rejection here
    // has already surfaced exactly one error — just abort (the page stays open).
    try {
      await capture.mutateAsync({
        referenceNo: tx.referenceNo,
        transaction: payload.transaction,
        workingPaper: payload.workingPaper,
        cesRows: payload.cesRows,
        findings: payload.findings,
      });
    } catch {
      return;
    }

    show('Compliance evaluation saved.', 'success');
    onDone();
  }

  // Sheet progress. `assessed` = a status has been chosen; `nonCompliant` = that
  // status is one of the non-compliant vocabulary entries.
  const assessedCount = rows.filter((r) => r.status.trim() !== '').length;
  const nonCompliantCount = rows.filter(
    (r) =>
      r.status.toLowerCase().includes('non-compliant') ||
      r.status.toLowerCase().includes('non-compliance'),
  ).length;

  // Render the criteria grouped by stage header (matching the working paper).
  let lastStage = '';

  return (
    <Card>
      <div className="section-head ces-page-head">
        <div>
          <h3 style={{ margin: 0 }}>Compliance Evaluation Sheet</h3>
          <div className="small">
            Capturing for <strong>{tx.referenceNo}</strong>
            {tx.division ? ` · ${tx.division}` : ''}
            {tx.supplier ? ` · ${tx.supplier}` : ''}
          </div>
          {/* Progress at a glance — how much of the sheet is assessed, and how
              much of that is non-compliant. Both figures recompute live. */}
          {rows.length > 0 && (
            <div className="ces-chipline">
              <span className="ces-chip">
                {assessedCount} of {rows.length} assessed
              </span>
              {nonCompliantCount > 0 && (
                <span
                  className="ces-chip"
                  style={{ color: 'var(--bad)', borderColor: 'var(--bad)' }}
                >
                  {nonCompliantCount} non-compliant
                </span>
              )}
            </div>
          )}
        </div>
        <div className="actions">
          {hasRealLink(tx) && (
            // A6: the OpenText document folder, reachable from the working-paper
            // surface (requires Microsoft Edge on the Eskom network). Gated on a
            // REAL link so a placeholder cell never renders a dead anchor.
            <a className="btn" href={tx.link} target="_blank" rel="noopener noreferrer">
              OpenText
            </a>
          )}
          <Button variant="ghost" onClick={onDone} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save evaluation'}
          </Button>
        </div>
      </div>

      {conflict && (
        <ConflictBanner
          message="This evaluation changed since you opened it"
          detail="Another reviewer saved a newer version. Reload to pick up their capture, then reapply your assessment over it."
          busy={saving}
          onReload={() => {
            setConflict(false);
            onDone(); // leave, so the page re-reads the fresh capture
          }}
        />
      )}

      {template ? (
        <div className="ces-note" style={{ borderLeft: '3px solid var(--accent, #00A3E0)' }}>
          Seeded from the <strong>{template.mechanism}</strong> working paper ({template.family}).
          Each criterion carries its rule-book default, so marking one non-compliant suggests the
          applicable compliance level — confirm or override per the evidence.
        </div>
      ) : (
        <div className="ces-note">
          No per-mechanism working paper could be resolved for this transaction — choose a
          procurement route to seed its compliance criteria.
        </div>
      )}

      {/* AI review (Surface A). Renders nothing unless the person is granted AND
          has AI Mode on, so with AI off this page is exactly what it was. */}
      <AiReviewPanel
        referenceNo={tx.referenceNo}
        criteria={aiCriteria}
        disabled={saving}
        onProposals={setProposals}
        onHolistic={setHolistic}
        onRunMeta={setAiRunMeta}
        decidedCount={decidedCount}
        decidedSeqs={decidedSeqs}
        onConfirm={confirmProposals}
        onOpenCitation={(c) => setOpenCitation(c)}
        sortByConfidence={sortByConfidence}
        onToggleSort={() => setSortByConfidence((v) => !v)}
      />

      {/* One evidence viewer for the page — see openCitation above. */}
      <EvidenceViewer
        open={openCitation !== null}
        onClose={() => setOpenCitation(null)}
        referenceNo={tx.referenceNo}
        citation={openCitation}
      />

      {/* The grey header block (R3 #1): population fields locked, the four
          OpenText-sourced fields editable. */}
      <div className="ces-form-grid ces-header-grid">
        {headerFields.map((f) =>
          f.editable && f.key ? (
            <label key={f.label}>
              {f.label}
              <input
                value={f.value}
                onChange={(e) => patchHeader(f.key as EditableHeaderKey, e.target.value)}
                placeholder="From the OpenText folder…"
                disabled={saving}
              />
            </label>
          ) : (
            <label key={f.label}>
              {f.label}
              {/* readOnly, NOT disabled: a locked population value must stay
                  selectable, copyable and tab-reachable (a disabled input is
                  none of those, and a reviewer often needs to copy the ref). */}
              <input
                className="ces-locked"
                value={f.value}
                readOnly
                title="Locked — from the bulk population"
              />
            </label>
          ),
        )}
      </div>

      <div className="ces-form-grid">
        {!template && (
          <label>
            Procurement route
            <Select
              ariaLabel="Procurement route"
              value={route}
              onChange={(v) => chooseRoute(v as CesRoute | '')}
              disabled={saving}
              options={[
                { value: '', label: '— Select a route —' },
                ...CES_ROUTES.map((r) => ({ value: r, label: r })),
              ]}
            />
          </label>
        )}
        <label>
          Compliance override
          <Select
            ariaLabel="Compliance override"
            value={override}
            onChange={handleOverrideChange}
            disabled={saving}
            options={[
              { value: '', label: 'No override (use rollup)' },
              ...TRANSACTION_COMPLIANCE_LEVELS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
        {cascadeCount !== null && (
          <div className="ces-cascade-prompt" role="status">
            <span>
              Set all {cascadeCount} non-compliant procedure{cascadeCount === 1 ? '' : 's'} to
              Compliant? You can still adjust individual procedures afterwards.
            </span>
            <span className="ces-cascade-actions">
              <Button onClick={applyCascade} disabled={saving}>
                Set all Compliant
              </Button>
              <Button variant="ghost" onClick={() => setCascadeCount(null)} disabled={saving}>
                Keep individual
              </Button>
            </span>
          </div>
        )}
        <label>
          Reviewer name
          <input
            aria-label="Reviewer name"
            value={reviewerName}
            disabled={saving}
            placeholder="Actual reviewer (if captured on their behalf)"
            onChange={(e) => setReviewerName(e.target.value)}
          />
        </label>
      </div>

      {/* Criteria checklist (auto-seeded from the working paper, or the chosen route). */}
      {rows.length > 0 ? (
        <div className="ces-criteria-wrap ces-page-criteria">
          <table className="ces-criteria-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Criterion</th>
                <th style={{ width: 220 }}>Status</th>
                <th>Finding</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r) => {
                // Stage headers assume stage-contiguous order. Sorted by
                // confidence that no longer holds, so they are suppressed rather
                // than emitted once per row.
                const showStage = !sortByConfidence && r.stage !== lastStage;
                lastStage = r.stage;
                return (
                  <RowFragment
                    key={r.seq}
                    row={r}
                    showStage={showStage}
                    disabled={saving}
                    onPatch={(patch) => patchRow(r.seq, patch)}
                    proposal={proposals.get(r.seq) ?? null}
                    confirmed={confirmed.has(r.seq)}
                    onOpenCitation={(c) =>
                      setOpenCitation({ ...c, rationale: proposals.get(r.seq)?.rationale })
                    }
                    onConfirmProposal={() => {
                      const p = proposals.get(r.seq);
                      if (!p) return;
                      // Confirming writes the SAME field the reviewer would have
                      // set by hand, and the finding text keeps the engine's
                      // reasoning so the working paper records why.
                      patchRow(r.seq, {
                        status: p.verdict as CesCriterionRow['status'],
                        finding: r.finding.trim() === '' ? p.rationale : r.finding,
                        ai: aiMetaFor(p),
                      });
                      setConfirmed((s) => new Set(s).add(r.seq));
                    }}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="ces-note" style={{ marginTop: 12 }}>
          Select a procurement route above to load its compliance criteria.
        </div>
      )}

      {/* Holistic findings (AI_ADDENDUM §3.3) — problems the engine raised that
          no criterion asks about. The checklist is a floor, not a ceiling, and
          the rubric keeps "Additional Comments" / "Gaps Identified" columns for
          exactly this. Rendered only after a run, and read-only: like every
          other proposal these are the engine's, and nothing is recorded unless
          the reviewer acts on it. */}
      {holistic.length > 0 && (
        <div className="ai-holistic" style={{ marginTop: 16 }}>
          <div className="section-head">
            <div>
              <h3>
                <Sparkles size={14} aria-hidden /> Raised outside the checklist
              </h3>
              <p className="small">
                {holistic.length} finding{holistic.length === 1 ? '' : 's'} the engine raised that no
                criterion covers. Record any you accept in Additional comments below.
              </p>
            </div>
          </div>
          {holistic.map((p, i) => (
            <div key={`holistic-${i}`} className="ai-holistic-item">
              <div className="ai-prop-head">
                <span className="ai-prop-tag">
                  <Sparkles size={9} aria-hidden /> AI
                </span>
                <Badge className={complianceClass(cesMapStatusToRegister(p.verdict))}>
                  {p.verdict}
                </Badge>
                <span className="small">{Math.round(p.confidence * 100)}% confidence</span>
              </div>
              <div className="ai-prop-why">{p.rationale}</div>
              {p.pfmaSection && <div className="ai-prop-pfma">{p.pfmaSection}</div>}
              <div className="ai-prop-cites">
                {p.citations.map((c) => (
                  <button
                    key={`${c.doc}-${c.page}`}
                    type="button"
                    className="ai-cite"
                    title={c.quote}
                    aria-label={`Open evidence: ${c.doc}, page ${c.page}`}
                    onClick={() => setOpenCitation({ ...c, rationale: p.rationale })}
                  >
                    {c.doc} · p.{c.page}
                  </button>
                ))}
              </div>
              {/* One click adds this finding to Additional comments, rather than
                  making the reviewer retype it. Idempotent — it will not append a
                  second copy of the same rationale. */}
              <Button
                size="sm"
                disabled={saving || additional.includes(p.rationale)}
                onClick={() =>
                  setAdditional((a) => {
                    const cite = p.citations[0] ? ` [${p.citations[0].doc} p.${p.citations[0].page}]` : '';
                    const line = `${p.verdict}: ${p.rationale}${cite}`;
                    return a.trim() === '' ? line : `${a}\n${line}`;
                  })
                }
              >
                {additional.includes(p.rationale) ? 'Added to comments' : 'Add to comments'}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Structured gaps (Transversal / Multisource): 5 fixed criteria above,
          then a reviewer-added gaps list — each rated + a contravention ref. */}
      {template?.supportsGaps && (
        <div className="ces-gaps" style={{ marginTop: 16 }}>
          <div className="section-head">
            <div>
              <h4 style={{ margin: 0 }}>{template.gapColumns?.description ?? 'Gaps Identified'}</h4>
              <div className="small">
                List each additional gap with its compliance level and the reference of
                contravention.
              </div>
            </div>
            <Button variant="ghost" onClick={addGap} disabled={saving}>
              Add gap
            </Button>
          </div>
          {gaps.length > 0 && (
            <div className="ces-criteria-wrap">
              <table className="ces-criteria-table ces-gaps-table">
                <thead>
                  <tr>
                    <th>{template.gapColumns?.description ?? 'Gap identified'}</th>
                    <th style={{ width: 220 }}>
                      {template.gapColumns?.complianceLevel ?? 'Compliance level'}
                    </th>
                    <th>{template.gapColumns?.contravention ?? 'Reference of contravention'}</th>
                    <th style={{ width: 44 }} />
                  </tr>
                </thead>
                <tbody>
                  {gaps.map((g) => (
                    <GapRowFragment
                      key={g.id}
                      gap={g}
                      disabled={saving}
                      onPatch={(patch) => patchGap(g.id, patch)}
                      onRemove={() => removeGap(g.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Additional findings — one classified finding per non-blank line. */}
      <div className="ces-form-grid" style={{ marginTop: 14 }}>
        <label className="full">
          Additional comments / findings
          <textarea
            value={additional}
            onChange={(e) => setAdditional(e.target.value)}
            placeholder="Overall comments, combined findings, or notes not covered above (one per line)."
            disabled={saving}
          />
        </label>
      </div>
    </Card>
  );
}

/** A stage header row (when the stage changes) followed by the criterion's editable cells. */
function RowFragment({
  row: r,
  showStage,
  disabled,
  onPatch,
  proposal,
  confirmed,
  onConfirmProposal,
  onOpenCitation,
}: {
  row: CesCriterionRow;
  showStage: boolean;
  disabled: boolean;
  onPatch: (patch: Partial<CesCriterionRow>) => void;
  /** Null whenever AI Mode is off or no run has happened — the row then renders
   *  exactly as it did before the engine existed. */
  proposal?: AiProposal | null;
  confirmed?: boolean;
  onConfirmProposal?: () => void;
  onOpenCitation?: (citation: AiProposal['citations'][number]) => void;
}): React.JSX.Element {
  return (
    <>
      {showStage && (
        <tr className="ces-stage-row">
          <td colSpan={4}>{r.stage}</td>
        </tr>
      )}
      <tr>
        <td>{r.seq}</td>
        <td>
          <div className="ces-criterion">{r.criterion}</div>
        </td>
        <td>
          <Select
            ariaLabel={`Status for criterion ${r.seq}`}
            value={r.status}
            onChange={(v) => onPatch({ status: v })}
            disabled={disabled}
            options={CES_STATUS_OPTIONS.map((s) => ({ value: s, label: s || '— Pending —' }))}
          />
          {/* Below the dropdown, never replacing it: the reviewer's control stays
              the reviewer's. */}
          {proposal && (
            <AiProposalBlock
              proposal={proposal}
              confirmed={confirmed === true}
              disabled={disabled}
              onConfirm={() => onConfirmProposal?.()}
              onOpenCitation={onOpenCitation}
            />
          )}
        </td>
        <td>
          <textarea
            value={r.finding}
            onChange={(e) => onPatch({ finding: e.target.value })}
            placeholder="Finding (if any)"
            disabled={disabled}
          />
        </td>
      </tr>
    </>
  );
}

/** One editable gap row: description, rated compliance level, and contravention reference. */
function GapRowFragment({
  gap,
  disabled,
  onPatch,
  onRemove,
}: {
  gap: GapDraft;
  disabled: boolean;
  onPatch: (patch: Partial<GapDraft>) => void;
  onRemove: () => void;
}): React.JSX.Element {
  return (
    <tr>
      <td>
        <textarea
          value={gap.description}
          onChange={(e) => onPatch({ description: e.target.value })}
          placeholder="Describe the gap"
          disabled={disabled}
        />
      </td>
      <td>
        <Select
          ariaLabel="Gap status"
          value={gap.status}
          onChange={(v) => onPatch({ status: v })}
          disabled={disabled}
          options={CES_STATUS_OPTIONS.map((s) => ({ value: s, label: s || '— Pending —' }))}
        />
      </td>
      <td>
        <input
          value={gap.contravention}
          onChange={(e) => onPatch({ contravention: e.target.value })}
          placeholder="e.g. 32-1034: 8.9.3"
          disabled={disabled}
        />
      </td>
      <td>
        <Button variant="ghost" onClick={onRemove} disabled={disabled} title="Remove gap">
          ✕
        </Button>
      </td>
    </tr>
  );
}
